/**
 * Staff Details & Personality Page
 * Refactored main component that imports modular components
 */
import { useState, useEffect } from 'react';
import { 
  Users, UserCheck, AlertCircle, Star, Search, PieChart, Lightbulb 
} from 'lucide-react';
import axios from 'axios';

// Import components from staff-details module
import {
  StaffCard,
  DetailModal,
  EditFormModal,
  SmartInsightsDashboard,
  TeamCompositionAnalytics
} from '../components/staff-details';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ===== Main Component =====
const StaffDetailsPage = () => {
  const [staffList, setStaffList] = useState([]);
  const [detailsList, setDetailsList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [viewModal, setViewModal] = useState({ open: false, staff: null, detail: null });
  const [editModal, setEditModal] = useState({ open: false, staff: null, detail: null });
  const [activeView, setActiveView] = useState('insights'); // 'insights', 'analytics' or 'list'
  
  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [staffRes, detailsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/staff`),
        axios.get(`${API}/staff-details/`),
        axios.get(`${API}/staff-details/summary`)
      ]);
      setStaffList(staffRes.data || []);
      setDetailsList(detailsRes.data || []);
      setSummary(summaryRes.data);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, []);
  
  // Get detail for staff
  const getDetailForStaff = (staffId) => {
    return detailsList.find(d => d.staff_id === staffId);
  };
  
  // Filter staff
  const filteredStaff = staffList.filter(s => {
    const matchSearch = s.name?.toLowerCase().includes(search.toLowerCase()) ||
                       s.position?.toLowerCase().includes(search.toLowerCase());
    const matchDept = !filterDept || s.department === filterDept;
    return matchSearch && matchDept;
  });
  
  // Get unique departments
  const departments = [...new Set(staffList.map(s => s.department).filter(Boolean))];
  
  // Handle save
  const handleSave = async (formData, detailId) => {
    if (detailId) {
      await axios.put(`${API}/staff-details/${detailId}`, formData);
    } else {
      await axios.post(`${API}/staff-details/`, formData);
    }
    await fetchData();
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="staff-details-page">
      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-700" /> 
            Staff Details & Personality
          </h1>
          <p className="text-gray-500 text-sm">Maklumat lengkap dan penilaian personaliti setiap staff</p>
        </div>
        
        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-xl p-1 overflow-x-auto">
          <button
            onClick={() => setActiveView('insights')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              activeView === 'insights' 
                ? 'bg-white text-emerald-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="view-insights-btn"
          >
            <Lightbulb className="w-4 h-4" />
            Smart Insights
          </button>
          <button
            onClick={() => setActiveView('analytics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              activeView === 'analytics' 
                ? 'bg-white text-blue-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="view-analytics-btn"
          >
            <PieChart className="w-4 h-4" />
            Team Analytics
          </button>
          <button
            onClick={() => setActiveView('list')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              activeView === 'list' 
                ? 'bg-white text-blue-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="view-list-btn"
          >
            <Users className="w-4 h-4" />
            Senarai Staff
          </button>
        </div>
      </div>
      
      {/* Smart Insights View */}
      {activeView === 'insights' && (
        <SmartInsightsDashboard 
          detailsList={detailsList} 
          staffList={staffList}
          summary={summary}
        />
      )}
      
      {/* Analytics View */}
      {activeView === 'analytics' && (
        <TeamCompositionAnalytics 
          summary={summary} 
          detailsList={detailsList} 
          staffList={staffList} 
        />
      )}
      
      {/* List View */}
      {activeView === 'list' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{summary.total_staff}</p>
                    <p className="text-xs text-gray-500">Jumlah Staff</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <UserCheck className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{summary.total_with_details}</p>
                    <p className="text-xs text-gray-500">Lengkap</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{summary.total_without_details}</p>
                    <p className="text-xs text-gray-500">Belum Lengkap</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Star className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{summary.leadership_distribution?.High || 0}</p>
                    <p className="text-xs text-gray-500">High Leadership</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Search & Filter */}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari nama atau jawatan..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="search-staff-input"
                />
              </div>
            </div>
            
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="filter-department-select"
            >
              <option value="">Semua Jabatan</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          
          {/* Staff Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredStaff.map(staff => (
              <StaffCard
                key={staff.id}
                staff={staff}
                detail={getDetailForStaff(staff.id)}
                onView={(s, d) => setViewModal({ open: true, staff: s, detail: d })}
                onEdit={(s, d) => setEditModal({ open: true, staff: s, detail: d })}
              />
            ))}
          </div>
          
          {filteredStaff.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Tiada staff dijumpai</p>
            </div>
          )}
        </>
      )}
      
      {/* Modals */}
      {viewModal.open && (
        <DetailModal
          staff={viewModal.staff}
          detail={viewModal.detail}
          onClose={() => setViewModal({ open: false, staff: null, detail: null })}
        />
      )}
      
      {editModal.open && (
        <EditFormModal
          staff={editModal.staff}
          detail={editModal.detail}
          onClose={() => setEditModal({ open: false, staff: null, detail: null })}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default StaffDetailsPage;
